/**
 * Store для управления микрофронтендами
 *
 * Инициализирует MFLoader и предоставляет методы для работы с MF.
 * Интегрирует иерархическую конфигурацию модулей (default → portal → org).
 */

import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'
import {
  getMFLoader,
  type MFExports,
  type MFLoader,
  type MFManifest,
  type PageContext,
  type UserContext,
} from '@/core/mf-loader'
import { type MergedModuleConfig, moduleConfigService } from '@/services/ModuleConfigService'
import { useAuthStore } from './auth'
import { useOrgStore } from './org'

export const useMFStore = defineStore('mf', () => {
  const loader = shallowRef<MFLoader | null>(null)
  const manifests = ref<MFManifest[]>([])
  const initialized = ref(false)
  const configLoaded = ref(false)
  const error = ref<string | null>(null)
  const currentOrgSlug = ref<string | null>(null)

  /**
   * Инициализировать MFLoader
   */
  async function init(baseUrl?: string) {
    if (initialized.value) {
      return manifests.value
    }

    const authStore = useAuthStore()

    // Определяем базовый URL для загрузки MF
    // В dev-режиме используем локальный путь, в prod - Object Storage
    const mfBaseUrl = baseUrl ?? getMFBaseUrl()

    console.log('[MFStore] Initializing with baseUrl:', mfBaseUrl)

    try {
      loader.value = getMFLoader(
        {
          baseUrl: mfBaseUrl,
          cacheBusting: import.meta.env.DEV,
        },
        () => authStore.token
      )

      manifests.value = await loader.value.init()
      initialized.value = true
      error.value = null

      console.log(
        '[MFStore] Loaded manifests:',
        manifests.value.map((m) => m.id)
      )

      return manifests.value
    } catch (e) {
      console.error('[MFStore] Failed to initialize:', e)
      error.value = e instanceof Error ? e.message : 'Failed to load microfrontends'
      return []
    }
  }

  /**
   * Загрузить конфигурацию модулей
   * Вызывается после инициализации MF и определения организации
   */
  async function loadModulesConfig(orgSlug?: string): Promise<void> {
    try {
      currentOrgSlug.value = orgSlug ?? null
      await moduleConfigService.loadAll(orgSlug)
      configLoaded.value = true
      console.log('[MFStore] Module configs loaded for org:', orgSlug || 'portal-only')
    } catch (e) {
      console.error('[MFStore] Failed to load module configs:', e)
    }
  }

  /**
   * Получить merged конфигурацию для модуля (sync, legacy API)
   */
  function getModuleConfig(moduleId: string): MergedModuleConfig {
    const legacyConfig = moduleConfigService.getLegacyMergedConfig(
      moduleId,
      'active',
      currentOrgSlug.value ?? undefined
    )
    // Конвертируем legacy формат в MergedModuleConfig
    return {
      id: moduleId,
      settings: legacyConfig.settings,
      sources: {
        mfDefault: legacyConfig.statusSource === 'default',
        org: legacyConfig.statusSource === 'org',
        project: false,
        course: false,
      },
    }
  }

  /**
   * Проверить, включён ли модуль
   * Проверяет:
   * 1. Org features (если есть) — модуль должен быть в списке features организации
   * 2. Module config (legacy) — статус модуля не должен быть 'inactive'
   */
  function isModuleEnabled(moduleId: string): boolean {
    // 1. Проверяем org features (из Admin API)
    const orgStore = useOrgStore()
    const orgFeatures = orgStore.features

    // Если у организации есть настройки features — проверяем по ним
    // features: { tagCloud: true, presentations: true, ... }
    if (Object.keys(orgFeatures).length > 0) {
      // Маппинг moduleId → feature key
      const featureMap: Record<string, keyof typeof orgFeatures> = {
        tagcloud: 'tagCloud',
        presentations: 'presentations',
        lms: 'lms',
        analytics: 'analytics',
        chat: 'chat',
      }

      const featureKey = featureMap[moduleId]
      if (featureKey) {
        // Если feature key существует и false — модуль отключён
        if (orgFeatures[featureKey] === false) {
          return false
        }
        // Если feature key не существует в объекте — модуль отключён
        // (только явно включённые features доступны)
        if (!(featureKey in orgFeatures) || !orgFeatures[featureKey]) {
          return false
        }
      }
      // Для модулей без маппинга (clock, consultant) — проверяем legacy config
    }

    // 2. Legacy проверка через ModuleConfigService
    return moduleConfigService.isModuleEnabled(
      moduleId,
      'active',
      currentOrgSlug.value ?? undefined
    )
  }

  /**
   * Computed: только включённые модули
   */
  const enabledManifests = computed(() => {
    if (!configLoaded.value) {
      // Если конфигурация ещё не загружена, показываем все
      return manifests.value
    }
    return manifests.value.filter((m) => isModuleEnabled(m.id))
  })

  /**
   * Инвалидировать кэш конфигураций
   */
  function invalidateConfigCache(orgSlug?: string) {
    moduleConfigService.invalidateCache(orgSlug)
    configLoaded.value = false
  }

  /**
   * Получить базовый URL для MF
   * В prod MF файлы находятся на том же домене в /mf/
   */
  function getMFBaseUrl(): string {
    // MF файлы доступны через /mf/ на том же домене
    // В dev — через Vite public директорию
    // В prod — через Object Storage website hosting (www.tlh.ru/mf/)
    return '/mf'
  }

  /**
   * Смонтировать MF в контейнер
   */
  async function mountMF(
    mfId: string,
    container: HTMLElement,
    options?: {
      locale?: string
      theme?: 'light' | 'dark'
      user?: UserContext
      basePath?: string
      settings?: Record<string, unknown>
    }
  ) {
    if (!loader.value) {
      throw new Error('MFLoader not initialized. Call init() first.')
    }

    await loader.value.mountMF(mfId, container, options)
  }

  /**
   * Смонтировать виджет MF
   */
  async function mountWidget(
    mfId: string,
    widgetId: string,
    container: HTMLElement,
    options?: {
      locale?: string
      theme?: 'light' | 'dark'
      user?: UserContext
      onNavigate?: (path: string) => void
      settings?: Record<string, unknown>
    }
  ): Promise<() => void> {
    if (!loader.value) {
      throw new Error('MFLoader not initialized. Call init() first.')
    }

    // Если settings не переданы явно, получаем их из конфигурации модуля
    const moduleConfig = getModuleConfig(mfId)
    const moduleSettings = options?.settings ?? moduleConfig.settings
    console.log(
      `[MFStore] mountWidget ${mfId}/${widgetId}, settings from config:`,
      JSON.stringify(moduleConfig, null, 2)
    )

    return loader.value.mountWidget(mfId, widgetId, container, {
      ...options,
      settings: moduleSettings,
    })
  }

  /**
   * Размонтировать MF
   */
  async function unmountMF(mfId: string) {
    if (loader.value) {
      await loader.value.unmountMF(mfId)
    }
  }

  /**
   * Размонтировать все MF
   */
  async function unmountAll() {
    if (loader.value) {
      await loader.value.unmountAll()
    }
  }

  /**
   * Обновить тему во всех MF
   */
  function updateTheme(theme: 'light' | 'dark') {
    if (loader.value) {
      loader.value.updateTheme(theme)
    }
  }

  /**
   * Обновить локаль во всех MF
   */
  function updateLocale(locale: string) {
    if (loader.value) {
      loader.value.updateLocale(locale)
    }
  }

  /**
   * Обновить контекст страницы (для глобальных виджетов типа consultant)
   * Передаёт информацию о текущей странице, активном MF, его данных
   */
  function updatePageContext(context: PageContext) {
    if (loader.value) {
      loader.value.updatePageContext(context)
      console.log('[MFStore] Page context updated:', context)
    }
  }

  /**
   * Получить манифест MF по ID
   */
  function getManifest(mfId: string): MFManifest | undefined {
    return manifests.value.find((m) => m.id === mfId)
  }

  /**
   * Проверить, есть ли MF
   */
  function hasMF(mfId: string): boolean {
    return manifests.value.some((m) => m.id === mfId)
  }

  /**
   * Загрузить MF модуль и получить его экспорты
   * Используется для доступа к функциям MF (например, mountSettingsEditor)
   */
  async function loadMF(mfId: string): Promise<MFExports | null> {
    if (!loader.value) {
      console.error('[MFStore] Loader not initialized')
      return null
    }
    return loader.value.loadMFModule(mfId)
  }

  // Хранилище функций размонтирования глобальных виджетов
  const globalWidgetUnmountFns = ref<Map<string, () => void>>(new Map())

  /**
   * Смонтировать все глобальные виджеты с autoMount: true
   * Например, consultant FAB который должен быть на всех страницах
   */
  async function mountAutoMountWidgets(options?: {
    locale?: string
    theme?: 'light' | 'dark'
  }): Promise<void> {
    if (!loader.value) {
      console.warn('[MFStore] Loader not initialized, skipping auto-mount')
      return
    }

    for (const manifest of manifests.value) {
      if (!manifest.widgets) continue

      for (const widget of manifest.widgets) {
        if (!widget.autoMount) continue

        // Проверяем, не смонтирован ли уже
        const widgetKey = `${manifest.id}/${widget.id}`
        if (globalWidgetUnmountFns.value.has(widgetKey)) {
          console.log(`[MFStore] Widget ${widgetKey} already mounted, skipping`)
          continue
        }

        try {
          console.log(`[MFStore] Auto-mounting widget: ${widgetKey}`)

          // Для глобальных виджетов (position: bottom-right и т.п.) создаём контейнер
          // MF сам решит, куда монтироваться (consultant создаёт свой контейнер)
          const container = document.createElement('div')
          container.id = `mf-widget-${manifest.id}-${widget.id}`
          container.style.cssText = 'position: fixed; z-index: 9999; pointer-events: none;'
          document.body.appendChild(container)

          const unmount = await loader.value.mountWidget(manifest.id, widget.id, container, {
            locale: options?.locale ?? 'ru',
            theme: options?.theme ?? 'dark',
          })

          // Оборачиваем unmount чтобы также удалить контейнер
          globalWidgetUnmountFns.value.set(widgetKey, () => {
            unmount()
            container.remove()
          })

          console.log(`[MFStore] Widget ${widgetKey} mounted successfully`)
        } catch (e) {
          console.error(`[MFStore] Failed to auto-mount widget ${widgetKey}:`, e)
        }
      }
    }
  }

  /**
   * Размонтировать все глобальные виджеты
   */
  function unmountAutoMountWidgets(): void {
    for (const [key, unmount] of globalWidgetUnmountFns.value) {
      try {
        unmount()
        console.log(`[MFStore] Unmounted widget: ${key}`)
      } catch (e) {
        console.error(`[MFStore] Failed to unmount widget ${key}:`, e)
      }
    }
    globalWidgetUnmountFns.value.clear()
  }

  return {
    // State
    loader,
    manifests,
    enabledManifests,
    initialized,
    configLoaded,
    error,
    currentOrgSlug,

    // Actions
    init,
    loadModulesConfig,
    getModuleConfig,
    isModuleEnabled,
    invalidateConfigCache,
    mountMF,
    mountWidget,
    unmountMF,
    unmountAll,
    updateTheme,
    updateLocale,
    updatePageContext,
    getManifest,
    hasMF,
    loadMF,
    mountAutoMountWidgets,
    unmountAutoMountWidgets,
  }
})
